import { Combobox } from '@headlessui/react';
import { i18n } from '@lingui/core';
import React from 'react';

type SearchboxInputProps<TValue> = {
    children?: React.ReactNode;
    className?: string;
    displayValue?: (item?: TValue) => string;
    name?: string;
    onChange: (value: string) => void;
    onFocus?: () => void;
    placeholder?: string;
};

function SearchboxInputInner<TValue>(
    props: SearchboxInputProps<TValue>,
    forwardedRef: React.ForwardedRef<HTMLInputElement>,
) {
    const {
        children,
        className = '',
        displayValue,
        name,
        onChange,
        onFocus,
        placeholder,
    } = props;

    const handleChange = React.useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const value = ev.target.value;
            onChange(value);
        },
        [onChange],
    );

    const hasSuffix = Boolean(children);

    const inputClass = `input--default ${hasSuffix ? 'input--suffix' : ''} ${
        placeholder ? 'input--placeholder' : ''
    }`;

    return (
        <>
            <Combobox.Label className="sr-only">
                {i18n.t({
                    id: 'searchbox.input.label',
                    message: 'Search',
                })}
            </Combobox.Label>
            <div className={`${className} input`}>
                <Combobox.Input
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    className={inputClass}
                    displayValue={displayValue}
                    enterKeyHint="search"
                    onChange={handleChange}
                    onFocus={onFocus}
                    name={name}
                    placeholder={placeholder}
                    ref={forwardedRef}
                    spellCheck="false"
                    type="search"
                />
                {children}
            </div>
        </>
    );
}

// Redeclare React.forwardRef to add support for generic types
// https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/forward_and_create_ref/#option-2---redeclare-forwardref
declare module 'react' {
    // eslint-disable-next-line @typescript-eslint/ban-types
    function forwardRef<T, P = {}>(
        render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
    ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

export const SearchboxInput = React.forwardRef(SearchboxInputInner);
